import { deleteJson, getJson, putJson } from '@/shared/api/apiClient';

/*
 * Where the viewer got to in each video, for the green progress bar across the
 * bottom of a "Continue watching" card and for resuming playback.
 *
 * Backed by `/api/v1/watch-progress`. This is the one of the five local services
 * with a real argument for staying local — a resume position is arguably a
 * property of the device, not the account — but "I was halfway through this on
 * my laptop" is exactly the thing a viewer expects to survive picking up their
 * phone, so it moved with the rest.
 *
 * It is also the chattiest of them: it updates during playback rather than on a
 * deliberate action. The band below is what keeps that from being a write per
 * tick, and it matters more now than it did against localStorage.
 */

const PATH = '/api/v1/watch-progress';

export type WatchProgress = {
  /** Seconds into the video. */
  position: number;
  /** Total length in seconds, so a fraction can be derived without the media. */
  duration: number;
  updatedAt: string;
};

export type WatchProgressMap = Record<string, WatchProgress>;

/* Below this the viewer has barely started and a sliver of a bar is just noise;
   above it they have effectively finished and a nearly-full bar implying
   "resume" is misleading. Outside the band we record nothing. */
const MIN_FRACTION = 0.02;
const MAX_FRACTION = 0.95;

export async function getWatchProgress(_userId: string): Promise<WatchProgressMap> {
  return getJson<WatchProgressMap>(PATH);
}

/**
 * Records a position. Returns the updated map so a caller can re-render without
 * a second read.
 *
 * Nonsense input is dropped here rather than sent: a duration of 0 is what a
 * <video> reports before metadata loads, and it arrives on essentially every
 * mount.
 */
export async function setWatchProgress(
  userId: string,
  videoId: string,
  position: number,
  duration: number,
): Promise<WatchProgressMap> {
  if (!Number.isFinite(position) || !Number.isFinite(duration) || duration <= 0) {
    return getWatchProgress(userId);
  }

  const fraction = position / duration;
  if (fraction < MIN_FRACTION || fraction > MAX_FRACTION) {
    // Finishing a video should clear it from "Continue watching" rather than
    // leave it pinned at 99%.
    return clearWatchProgress(userId, videoId);
  }

  return putJson<WatchProgressMap>(`${PATH}/${encodeURIComponent(videoId)}`, {
    position,
    duration,
  });
}

export async function clearWatchProgress(
  _userId: string,
  videoId: string,
): Promise<WatchProgressMap> {
  return deleteJson<WatchProgressMap>(`${PATH}/${encodeURIComponent(videoId)}`);
}

/** 0–1, or undefined when there is nothing recorded. */
export function watchFraction(
  progress: WatchProgressMap,
  videoId: string,
): number | undefined {
  const entry = progress[videoId];
  if (!entry || entry.duration <= 0) return undefined;
  return Math.min(1, Math.max(0, entry.position / entry.duration));
}
